import type { Asset } from '../services/asset.api';

/**
 * Health score is now COMPUTED ON THE BACKEND and returned in the asset
 * payload. This file no longer contains any business logic — it is a
 * thin passthrough kept for API compatibility with existing callers.
 *
 * Source of truth: backend-homeiq/src/services/business/health.service.ts
 */
export function calculateHealthScore(asset: Asset): number {
  // Backend returns 0..10. Some legacy data is 0..100 — normalize here.
  const raw = (asset as unknown as { healthScore?: number }).healthScore;
  if (typeof raw === 'number' && raw > 0) {
    return raw > 10 ? Math.round(raw / 10) : raw;
  }
  return 7; // unknown — neutral default
}

// ── Color helpers (display-only, NOT business logic) ──

export function getHealthColor(score: number): string {
  if (score >= 8) return 'text-green-600 dark:text-green-400';
  if (score >= 6) return 'text-yellow-600 dark:text-yellow-400';
  if (score >= 4) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400';
}

export function getHealthBgColor(score: number): string {
  if (score >= 8) return 'bg-green-500';
  if (score >= 6) return 'bg-yellow-500';
  if (score >= 4) return 'bg-orange-500';
  return 'bg-red-500';
}

export function getHealthLabel(score: number): string {
  if (score >= 8) return 'Excellent';
  if (score >= 6) return 'Good';
  if (score >= 4) return 'Fair';
  return 'Needs Attention';
}

// ── Warranty status helper ──

export interface WarrantyStatus {
  label: string;
  variant: 'active' | 'expiring' | 'expired' | 'unknown';
  daysLeft: number;
}

/**
 * Warranty status is now computed on the backend and returned on the
 * asset as `warrantyStatus` + `daysUntilWarrantyExpiry`. This helper
 * formats that for display only — no threshold logic remains here.
 */
export function getWarrantyStatus(
  expiresAt?: string | null,
  serverStatus?: 'active' | 'expiringSoon' | 'expired' | 'unknown' | null,
  daysUntilWarrantyExpiry?: number | null,
): WarrantyStatus {
  if (!expiresAt && !serverStatus) {
    return { label: 'No warranty info', variant: 'unknown', daysLeft: 0 };
  }

  const days =
    typeof daysUntilWarrantyExpiry === 'number'
      ? daysUntilWarrantyExpiry
      : expiresAt
        ? Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
        : 0;

  const status = serverStatus ?? (days < 0 ? 'expired' : 'active');

  if (status === 'expired') return { label: `Expired ${Math.abs(days)} days ago`, variant: 'expired', daysLeft: days };
  if (status === 'expiringSoon')
    return { label: `${days} days left — expiring soon`, variant: 'expiring', daysLeft: days };
  if (status === 'unknown') return { label: 'No warranty info', variant: 'unknown', daysLeft: days };
  return { label: `${days} days left`, variant: 'active', daysLeft: days };
}

